import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
  ReactNode,
} from "react";
import { Conversation, Message } from "@/types";
import { useProjects } from "@/hooks/useProjects";
import { useConversationHistory } from "@/hooks/useConversationHistory";
import { getApiConversationId } from "@/api/conversation/conversation";

interface DataContextType {
  conversations: Conversation[];
  currentConversationId: string | null;
  addMessage: (conversationId: string, message: Omit<Message, "id">) => string;
  createConversation: (projectId?: string) => string;
  switchConversation: (conversationId: string) => void;
  clearCurrentConversation: () => void;
  updateMessage: (
    conversationId: string,
    messageId: string,
    updates: Partial<Message>,
  ) => void;
  getCurrentConversation: () => Conversation | null;
  getConversationsByProjectId: (projectId: string) => Conversation[];
  removeConversation: (conversationId: string) => void;
  cleanupEmptyConversation: (conversationId?: string) => void;
  clearChatInput: () => void;
  loadConversationHistory: (projectId: string) => Promise<string | null>;
  isLoadingConversation: boolean;
  conversationError: string | null;
}

const DataContext = createContext<DataContextType | undefined>(undefined);

interface DataProviderProps {
  children: ReactNode;
}

export function DataProvider({ children }: DataProviderProps) {
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [currentConversationId, setCurrentConversationId] = useState<
    string | null
  >(null);

  // Load projects on mount
  const { fetchProjects } = useProjects();
  const {
    isLoading: isLoadingConversation,
    error: conversationError,
    fetchConversationHistory,
  } = useConversationHistory();

  useEffect(() => {
    // Fetch projects when the app loads (after authentication)
    // The hook will skip if already loaded
    fetchProjects().catch((error) => {
      console.error("Failed to load projects:", error);
    });
  }, [fetchProjects]);

  /**
   * Load conversation history for a project from the API.
   * Fetches events from Bedrock AgentCore and adds them to the conversations state.
   * If a conversation for this project already exists (loaded from API), it will be replaced.
   *
   * @returns The conversation ID if history was loaded, or null if no history exists
   */
  const loadConversationHistory = useCallback(
    async (projectId: string): Promise<string | null> => {
      const result = await fetchConversationHistory(projectId);
      if (result && result.conversation.messages.length > 0) {
        setConversations((prev) => {
          // Remove any existing API-loaded conversation for this project
          const filtered = prev.filter(
            (conv) => conv.id !== getApiConversationId(projectId),
          );
          return [...filtered, result.conversation];
        });
        return result.conversation.id;
      }
      return null;
    },
    [fetchConversationHistory],
  );

  const generateId = () =>
    Date.now().toString() + Math.random().toString(36).substr(2, 9);

  const createConversation = (projectId?: string): string => {
    const newConversation: Conversation = {
      id: generateId(),
      messages: [],
      createdAt: new Date(),
      projectId,
    };

    setConversations((prev) => [...prev, newConversation]);
    setCurrentConversationId(newConversation.id);

    return newConversation.id;
  };

  // switchConversation is now stable — just sets the ID, no state reads
  const switchConversation = useCallback((conversationId: string) => {
    setCurrentConversationId(conversationId);
  }, []);

  const clearCurrentConversation = useCallback(() => {
    setCurrentConversationId(null);
  }, []);

  // Mirror of conversations for stable-identity callbacks below.
  const conversationsRef = useRef(conversations);
  conversationsRef.current = conversations;

  // Stable identity — prevents consumer effects from re-running on every message update.
  const addMessage = useCallback(
    (conversationId: string, message: Omit<Message, "id">): string => {
      const messageWithId: Message = {
        ...message,
        id: generateId(),
      };

      setConversations((prev) =>
        prev.map((conv) =>
          conv.id === conversationId
            ? { ...conv, messages: [...conv.messages, messageWithId] }
            : conv,
        ),
      );

      // Auto-generate conversation title from first user message
      if (
        message.isUser &&
        !conversationsRef.current.find((c) => c.id === conversationId)?.title
      ) {
        const title =
          message.content.slice(0, 50) +
          (message.content.length > 50 ? "..." : "");
        setConversations((prev) =>
          prev.map((conv) =>
            conv.id === conversationId ? { ...conv, title } : conv,
          ),
        );
      }

      return messageWithId.id;
    },
    [],
  );

  // Stable identity — prevents consumer effects from re-running on every message update.
  const updateMessage = useCallback(
    (conversationId: string, messageId: string, updates: Partial<Message>) => {
      setConversations((prev) =>
        prev.map((conv) =>
          conv.id === conversationId
            ? {
                ...conv,
                messages: conv.messages.map((msg) =>
                  msg.id === messageId ? { ...msg, ...updates } : msg,
                ),
              }
            : conv,
        ),
      );
    },
    [],
  );

  const getCurrentConversation = (): Conversation | null => {
    return (
      conversations.find((conv) => conv.id === currentConversationId) || null
    );
  };

  const getConversationsByProjectId = useCallback(
    (projectId: string): Conversation[] => {
      return conversationsRef.current
        .filter((conv) => conv.projectId === projectId)
        .sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
    },
    [],
  );

  const removeConversation = (conversationId: string) => {
    setConversations((prev) =>
      prev.filter((conv) => conv.id !== conversationId),
    );

    // If we're removing the current conversation, clear it
    if (currentConversationId === conversationId) {
      setCurrentConversationId(null);
    }
  };

  const cleanupEmptyConversation = (conversationId?: string) => {
    const targetId = conversationId || currentConversationId;
    if (!targetId) return;

    const conversation = conversations.find((conv) => conv.id === targetId);
    if (conversation && conversation.messages.length === 0) {
      removeConversation(targetId);
    }
  };

  const clearChatInput = () => {
    // Dispatch a custom event to notify ChatInput components to clear their text
    window.dispatchEvent(new CustomEvent("clearChatInput"));
  };

  const value: DataContextType = {
    conversations,
    currentConversationId,
    loadConversationHistory,
    isLoadingConversation,
    conversationError,
    addMessage,
    createConversation,
    switchConversation,
    clearCurrentConversation,
    updateMessage,
    getCurrentConversation,
    getConversationsByProjectId,
    removeConversation,
    cleanupEmptyConversation,
    clearChatInput,
  };

  return <DataContext.Provider value={value}>{children}</DataContext.Provider>;
}

export function useData() {
  const context = useContext(DataContext);
  if (context === undefined) {
    throw new Error("useData must be used within a DataProvider");
  }
  return context;
}
